import React, { useState, useEffect } from "react";
import Select from "react-select";
import LookupsAPI from "../../API/lookups.api";
import {
    getReactSelectTheme,
    getReactSelectStyling,
} from "../../utils/ReactSelectStyles";
import { ErrorAlert } from "../../utils/Alerts";
import { useTranslation } from "react-i18next";
import {
    FIELD_TYPES,
    BOOLEAN_DROPDOWN,
    SORTING_DIRECTION_OPTIONS,
} from "../../utils/constants";
import VerticalRadioButtons from "../RadioButtons/VerticalRadioButtons";
import CheckBox from "../CheckBox/CheckBox";
import InformationTooltip from "../Tooltips/InformationTooltip";
import ValidationError from "../Shared/ValidationError";

const ModelSupportedFeatures = ({
    modelOptions,
    setModelOptions,
    parametersSettings,
    activeUnitIndices,
    isEdit = false,
    errors,
    isFormSubmitted,
}) => {
    const [supportedAccessories, setSupportedAccessories] = useState([]);
    const [scannerTypes, setScannerTypes] = useState([]);
    const [parameterOptions, setParameterOptions] = useState([]);

    const { t } = useTranslation();

    const SUPPORTED_OPTIONS = [
        {
            label: t("supportedDevices"),
            name: "supportedDevices",
            isMulti: true,
            options: scannerTypes,
            type: FIELD_TYPES.Dropdown,
            value: modelOptions?.supportedDevices,
        },
        {
            label: t("supportedAccessories"),
            name: "supportedAccessories",
            isMulti: true,
            options: supportedAccessories,
            type: FIELD_TYPES.Dropdown,
            value: modelOptions?.supportedAccessories,
        },
    ];

    const ADVANCED_OPTIONS = [
        {
            innerLabel: t("enforceOutlier"),
            name: "enforceOutlierDetection",
            type: FIELD_TYPES.CheckBoxes,
            value: modelOptions?.enforceOutlierDetection ?? false,
        },
        {
            innerLabel: t("enableDynamicSorting"),
            name: "isDynamicSorting",
            type: FIELD_TYPES.CheckBoxes,
            className: modelOptions?.isDynamicSorting && "border-b pb-3",
            value: modelOptions?.isDynamicSorting ?? false,
        },
        {
            hidden: !modelOptions?.isDynamicSorting,
            indentation: true,
            label: t("sortDirection"),
            name: "sortingDirection",
            type: FIELD_TYPES.RadioButton,
            options: Object.values(SORTING_DIRECTION_OPTIONS),
            accessor: SORTING_DIRECTION_OPTIONS,
            value:
                modelOptions?.sortingDirection || SORTING_DIRECTION_OPTIONS.ASC,
        },
        {
            hidden: !modelOptions?.isDynamicSorting,
            indentation: true,
            label: t("displayView"),
            name: "isSortingByValues",
            type: FIELD_TYPES.RadioButton,
            options: Object.values(BOOLEAN_DROPDOWN),
            accessor: BOOLEAN_DROPDOWN,
            value: modelOptions?.isSortingByValues || BOOLEAN_DROPDOWN.false,
        },
        {
            hidden: !modelOptions?.isDynamicSorting,
            indentation: true,
            label: t("parameters"),
            tooltip: t("dynamicParametersTooltip"),
            name: "dynamicSortedParameterNames",
            isMulti: true,
            options: isEdit ? modelOptions.parameters : parameterOptions,
            type: FIELD_TYPES.Dropdown,
            isOptionDisabled: (parameter) => {
                const selectedItem =
                    modelOptions.dynamicSortedParameterNames.length === 0
                        ? null
                        : modelOptions.dynamicSortedParameterNames.find(
                              (item) => !item.isDeleted
                          );
                if (
                    selectedItem &&
                    parameter.unitName !== selectedItem.unitName
                )
                    return parameter;
            },
            value: modelOptions?.dynamicSortedParameterNames || [],
        },
    ];

    useEffect(() => {
        fetchSupportedAccessories();
        fetchScannerTypes();
    }, []);

    useEffect(() => {
        if (parametersSettings) {
            setParameterOptions(
                parametersSettings
                    ?.map((parameter, index) => {
                        const unitCount = parameter.parameterDetails.length;
                        const activeUnitIndex =
                            unitCount < 2 ? 0 : activeUnitIndices[index];
                        return {
                            label: parameter.name,
                            value: parameter.name,
                            unitName:
                                parameter.parameterDetails[activeUnitIndex]
                                    ?.unit?.label,
                        };
                    })
                    ?.filter(
                        (parameter) => parameter.value && parameter.unitName
                    )
            );
        }
    }, [parametersSettings]);

    const fetchSupportedAccessories = async () => {
        try {
            const result = await LookupsAPI.getSupportedAccessories();
            setSupportedAccessories(result);
        } catch (responseError) {
            ErrorAlert(responseError.message || responseError);
        }
    };

    const fetchScannerTypes = async () => {
        const result = await LookupsAPI.getScannerTypes();
        setScannerTypes(result);
    };

    const handleSelectChange = (val, action) => {
        if (isEdit) {
            if (action.action === "remove-value") {
                let found = modelOptions[action.name].find(
                    (elem) => elem.value === action.removedValue.value
                );
                let newModelOptions = modelOptions[action.name];
                if (found) {
                    newModelOptions = newModelOptions.map((option, index) => {
                        if (option.value === action.removedValue.value) {
                            return {
                                ...newModelOptions[index],
                                isDeleted: true,
                            };
                        }
                        return option;
                    });
                    setModelOptions((prev) => ({
                        ...prev,
                        [action.name]: newModelOptions,
                    }));
                } else
                    newModelOptions = newModelOptions.filter(
                        (acc) => acc.id !== action.removedValue.value
                    );
            } else {
                setModelOptions({
                    ...modelOptions,
                    [action.name]: val.map((v) => v),
                });
            }
        } else {
            setModelOptions((prev) => ({
                ...prev,
                [action.name]: val.map((v) => v),
            }));
        }
    };

    const handleRadioChange = (e, accessor = null) => {
        const changeValue = e.target.value;
        setModelOptions((prev) => ({
            ...prev,
            [e.target.name]: accessor ? accessor[changeValue] : changeValue,
        }));
    };

    const handleCheckBoxChange = (e) => {
        setModelOptions((prev) => ({
            ...prev,
            [e.target.name]: !prev[e.target.name],
        }));
    };

    const modelOptionsStateMachine = (option) => {
        switch (option.type) {
            case FIELD_TYPES.Dropdown:
                return (
                    <>
                        <Select
                            onChange={handleSelectChange}
                            isMulti={option.isMulti}
                            isClearable={false}
                            isSearchable
                            options={option.options}
                            placeholder={`Select ${option.label}...`}
                            name={option.name}
                            theme={getReactSelectTheme}
                            value={option.value?.filter((e) => !e.isDeleted)}
                            styles={getReactSelectStyling({
                                isMultiSelect: option.isMulti,
                            })}
                            isOptionDisabled={option?.isOptionDisabled}
                            menuPlacement="auto"
                            menuShouldScrollIntoView={false}
                        />
                        {isFormSubmitted && (
                            <ValidationError
                                errorMessage={errors[option.name]}
                            />
                        )}
                    </>
                );
            case FIELD_TYPES.CheckBoxes:
                return (
                    <CheckBox
                        name={option.name}
                        checked={option.value}
                        label={option.innerLabel}
                        onChangeHandler={handleCheckBoxChange}
                        containerClassName="ml-3"
                        checkBoxClassName="top-2"
                    />
                );
            case FIELD_TYPES.RadioButton:
                return (
                    <>
                        <div className="flex flex-col">
                            <VerticalRadioButtons
                                name={option.name}
                                options={option.options}
                                onChangeHandler={(e) =>
                                    handleRadioChange(e, option.accessor)
                                }
                                checkedReference={option.value.label}
                            />
                        </div>
                    </>
                );
            default:
                return;
        }
    };

    const renderFields = (fields) => {
        return fields.map((option, index) => {
            if (!option.hidden) {
                return (
                    <div
                        className={`input-group ${
                            option.indentation ? "ml-10" : ""
                        } ${option.className ? option.className : ""}`}
                        key={`${option.name} ${index}`}
                    >
                        <div className="flex gap-1">
                            <label className="uppercase">{option.label}</label>
                            {option.tooltip && (
                                <InformationTooltip
                                    message={option.tooltip}
                                    className="text-xs"
                                />
                            )}
                        </div>

                        {modelOptionsStateMachine(option)}
                    </div>
                );
            }
        });
    };

    return (
        <>
            {renderFields(SUPPORTED_OPTIONS)}
            <div className="input-group">
                <label className="uppercase">{t("advancedSettings")}</label>
            </div>
            {renderFields(ADVANCED_OPTIONS)}
        </>
    );
};

export default ModelSupportedFeatures;
